import * as React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { EmptyList } from "security-app/components/common";
import { GLCheckbox, RowTitleButton } from "common/components";
import { useColumnTogglerContext } from "security-app/components/common/ColumnToggler";
import { usePagination, useSelectedRows, useSelectedRowsDispatch, useModalContext } from "common/contexts";
import { useGetPriorities } from "security-app/hooks/useAssetsAPI";
import PriorityCell from "security-app/components/common/PriorityCell";
import { MachineData, UserData } from "./AssetDetails";
import RowActions from "./RowActions";
import getVisibleDetailColumnsCount, { STATIC_COLUMNS_COUNT } from "./utils/getVisibleDetailColumnsCount";
import useAssetPanel from "./hooks/useAssetPanel";
const ListRows = ({
  assets,
  showDetailsFor,
  showSelection = true,
  showDeleteAction = true,
  showEditAction = true
}) => {
  var _a, _b, _c, _d;
  const localPagination = usePagination();
  const selectedAssets = useSelectedRows();
  const selectedAssetsDispatch = useSelectedRowsDispatch();
  const { columns } = useColumnTogglerContext();
  const { setModal, setEntity } = useModalContext();
  const { openAssetDetails } = useAssetPanel();
  const { priorities } = useGetPriorities(true);
  const { id: assetId } = useParams();
  const showPriorityColumn = (_b = (_a = columns == null ? void 0 : columns.priority) == null ? void 0 : _a.visible) != null ? _b : true;
  const showCategoryColumn = (_d = (_c = columns == null ? void 0 : columns.category) == null ? void 0 : _c.visible) != null ? _d : false;
  const detailColumnsCount = useMemo(() => getVisibleDetailColumnsCount(columns || {}), [columns]);
  const staticColumnsCount = STATIC_COLUMNS_COUNT - (showSelection ? 0 : 1);
  const colSpan = detailColumnsCount + staticColumnsCount + (showPriorityColumn ? 1 : 0);
  const selectedType = Object.keys(showDetailsFor).find((key) => showDetailsFor[key] === true);
  const filteredAssets = useMemo(
    () => assets.filter((asset) => asset.details.type === selectedType),
    [assets, selectedType]
  );
  const autoOpenedRef = useRef(null);
  useEffect(() => {
    if (assetId && autoOpenedRef.current !== assetId) {
      const asset = filteredAssets.find((a) => a.id === assetId);
      if (asset) {
        autoOpenedRef.current = assetId;
        openAssetDetails(asset);
      }
    }
  }, [assetId, filteredAssets, openAssetDetails]);
  const onChange = (asset) => (e) => {
    if (e.target.checked) {
      selectedAssetsDispatch({ type: "add", payload: [asset] });
    } else {
      selectedAssetsDispatch({ type: "remove", payload: [asset] });
    }
  };
  const isSelected = (asset) => !!selectedAssets.find((sAssets) => asset.id === sAssets.id);
  const openAssetModal = (asset) => {
    setEntity({ asset });
    setModal(asset.details.type === "machine" ? "MACHINE-FORM" : "USER-FORM");
  };
  const openDetailsDrawer = (asset) => {
    openAssetDetails(asset);
  };
  return assets.length < 1 ? /* @__PURE__ */ React.createElement(
    EmptyList,
    {
      query: localPagination.query,
      filters: localPagination.filters,
      noSearchResultText: "No assets found for the provided criteria",
      noEntitiesExist: "Currently there are no assets to list",
      colSpan
    }
  ) : /* @__PURE__ */ React.createElement(React.Fragment, null, filteredAssets.map((asset) => /* @__PURE__ */ React.createElement("tr", { key: `asset-row-${asset.id}`, "data-testid": "asset-row" }, showSelection && /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(GLCheckbox, { checked: isSelected(asset), onChange: onChange(asset) })), /* @__PURE__ */ React.createElement("td", { "data-testid": "asset-name" }, /* @__PURE__ */ React.createElement(RowTitleButton, { onClick: () => openDetailsDrawer(asset) }, asset.name)), showPriorityColumn && /* @__PURE__ */ React.createElement("td", { "data-testid": "asset-priority" }, /* @__PURE__ */ React.createElement(PriorityCell, { priorityValue: asset.priority, priorities })), showCategoryColumn && /* @__PURE__ */ React.createElement("td", { "data-testid": "asset-category" }, (asset.category || []).join(", ")), /* @__PURE__ */ React.createElement(
    MachineData,
    {
      assetDetails: {
        ...asset.details,
        risk_score_normalized: asset.risk_score_normalized,
        vulnerabilities: asset.vulnerabilities
      },
      showDetails: showDetailsFor.machine
    }
  ), /* @__PURE__ */ React.createElement(
    UserData,
    {
      assetDetails: {
        ...asset.details,
        risk_score_normalized: asset.risk_score_normalized
      },
      showDetails: showDetailsFor.user
    }
  ), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    RowActions,
    {
      asset,
      openAssetModal,
      showDelete: showDeleteAction,
      showEdit: showEditAction
    }
  )))));
};
export default ListRows;
